import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import { ErrorModal } from "@/components/shared/modal/error-modal";
import Modal from "@/components/shared/modal/modal";
import {
	Button,
	CircularProgress,
	Grid,
	Snackbar,
	TextField,
	Theme,
	createStyles,
	makeStyles,
} from "@material-ui/core";
import { Alert, Color } from "@material-ui/lab";
import { INITIAL_VALUES } from "../../models/FormInitialValues";
import { WorkerType } from "../../models/WorkerType";
import {
	getWorkerTypeById,
	updateWorkerType,
} from "../../repositories/WorkerTypesRepository";

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		modalBody: {},
		title: {
			fontSize: 15,
		},
		chip: {
			margin: 2,
		},
		errorLabel: {
			fontSize: 12,
			color: "#F44336",
		},
	}),
);

type Props = {
	workerTypeIdForEdit: number | null;
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
};

export const EditWorkerType = ({
	workerTypeIdForEdit,
	show,
	onHide,
}: Props) => {
	const classes = useStyles();
	const [openAlertMessage, setOpenAlertMessage] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [workerTypeForEdit, setWorkerTypeForEdit] = useState<WorkerType | null>(
		null,
	);
	const [message, setMessage] = useState("");
	const [messageType, setMessageType] = useState<Color | undefined>(undefined);

	const [error, setError] = useState<string | null>(null);
	const [showErrorModal, setShowErrorModal] = useState(false);
	const {
		control,
		setValue,
		handleSubmit,
		formState: { errors },
	} = useForm<WorkerType>({ defaultValues: INITIAL_VALUES });

	const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setOpenAlertMessage(false);
	};

	const onSubmit: SubmitHandler<WorkerType> = async (data) => {
		setIsSubmitLoading(true);
		try {
			const res = await updateWorkerType(workerTypeIdForEdit, data);
			if (res.ok) {
				onHide(true);
				setMessage("The worker type was updated successfully");
				setMessageType("success");
			}
		} catch (e) {
			setMessage(`Error, please try again`);
			setMessageType("error");

			setError(e as string);
			setShowErrorModal(true);
		}
		setIsSubmitLoading(false);
		setOpenAlertMessage(true);
	};

	useEffect(() => {
		if (show) {
			setIsSubmitLoading(false);
			(async () => {
				setIsLoading(true);
				const workerType = await getWorkerTypeById(workerTypeIdForEdit);
				if (workerType.data.success !== true) {
					onHide(true);
					setMessage("The worker type doesn't exists");
					setMessageType("error");
					setOpenAlertMessage(true);
				} else {
					setWorkerTypeForEdit(workerType?.data?.result);
				}
				setIsLoading(false);
			})();
		}
	}, [show]);

	useEffect(() => {
		if (show && workerTypeForEdit) {
			const { workerTypeId, workerTypeName } = workerTypeForEdit!;
			setValue("workerTypeId", workerTypeId);
			setValue("workerTypeName", workerTypeName);
		}
	}, [workerTypeForEdit]);

	return (
		<>
			<Modal
				open={show}
				handleClose={() => {
					onHide(true);
				}}
				id="editWorkerType"
				title="Edit worker type"
				maxWidth="md"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<Modal.Content>
						{isLoading && (
							<Grid
								container
								justifyContent="center"
								alignItems="center"
								style={{ minHeight: "50px" }}
							>
								{/* <LazyLoading /> */}
								<CircularProgress size="2rem" />
							</Grid>
						)}

						{!isLoading && (
							<>
								<Grid container spacing={4}>
									<Grid item md={12} xs={12}>
										<Controller
											name="workerTypeName"
											control={control}
											render={({ field }) => (
												<TextField
													label="Name"
													value={field.value}
													onChange={field.onChange}
													variant="outlined"
													error={!!errors.workerTypeName}
													fullWidth
													margin="dense"
												/>
											)}
										/>

										{errors.workerTypeName?.type === "required" && (
											<span className={classes.errorLabel}>
												Name is required
											</span>
										)}
									</Grid>
								</Grid>
							</>
						)}
					</Modal.Content>
					<Modal.Actions style={{ padding: "2rem" }}>
						<Grid container spacing={2} justifyContent="flex-end">
							<Grid item md={3} xs={12} style={{ margin: 0 }}>
								<Button
									fullWidth
									variant="contained"
									color="default"
									onClick={() => {
										onHide(false);
										setWorkerTypeForEdit(null);
									}}
								>
									Cancel
								</Button>
							</Grid>
							<Grid item md={3} xs={12} style={{ margin: 0 }}>
								<Button
									fullWidth
									startIcon={
										isSubmitLoading ? <CircularProgress size="1rem" /> : <></>
									}
									disabled={isSubmitLoading}
									variant="contained"
									color="primary"
									type="submit"
								>
									Save
								</Button>
							</Grid>
						</Grid>
					</Modal.Actions>
				</form>
			</Modal>
			<Snackbar
				open={openAlertMessage}
				autoHideDuration={2500}
				onClose={handleClose}
			>
				<Alert severity={messageType} onClose={handleClose}>
					{message}
				</Alert>
			</Snackbar>
			<ErrorModal onHide={() => setError(null)} error={error} />
		</>
	);
};
